import { Formik } from 'components/form/formik';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { PlusIcon } from '@phosphor-icons/react';

import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { Button } from 'components/button/Button';
import { InputField } from 'components/input/InputField';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { getDisplayError } from '../../../utils/get-display-error';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { CheckboxField } from '../../../components/checkbox/CheckboxField';
import { nullthrows } from '../../../utils/invariant';
import { useTranslation } from '../../../contexts/translation-context';
import { captureException } from '@sentry/react';
import { updateCarrierPortalContact } from '../models';

const updateContactSchema = Yup.object().shape({
  name: Yup.string().min(1, 'errors.required').required('errors.required'),
  email: Yup.string().email('errors.invalidEmail'),
  phone: Yup.string().phone(),
  language: Yup.mixed().nullable().required('errors.required'),
  shouldReceiveTransportOrders: Yup.boolean(),
  shouldReceiveSelfBillings: Yup.boolean(),
});

export const CarrierPortalUpdateContactPage = () => {
  const navigate = useNavigate();
  const { contactId: _contactId } = useParams<{ contactId: string }>();
  const contactId = nullthrows(_contactId, 'contact id not defined');
  const { supplier, carrierId, refreshData: refetchSupplierData } = useCarrierPortalSupplier();
  const { i18n } = useTranslation();

  const contact = nullthrows(
    supplier.contacts.find((c) => c.id === +contactId),
    i18n('carrierPortal.contacts.notFound'),
  );

  const initialValues = {
    name: contact.name,
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    language: LANGUAGE_VALUES.find((v) => v.key === contact.language),
    function: contact.function ?? '',
    shouldReceiveTransportOrders: contact.shouldReceiveTransportOrders,
    shouldReceiveSelfBillings: contact.shouldReceiveSelfBillings,
  };

  return (
    <>
      <h1 className="heading-one m-4">{i18n('carrierPortal.contacts.updateContact')}</h1>

      <div className="card p-4 mx-4">
        <Formik
          initialValues={initialValues}
          validationSchema={updateContactSchema}
          onSubmit={async (values) => {
            try {
              await updateCarrierPortalContact(carrierId, contact.id, {
                name: values.name,
                email: values.email,
                phone: values.phone,
                function: values.function,
                language: values.language?.key,
                shouldReceiveTransportOrders: values.shouldReceiveTransportOrders,
                shouldReceiveSelfBillings: values.shouldReceiveSelfBillings,
              });
              await refetchSupplierData();
              toast.success(i18n('carrierPortal.contacts.updateContactSuccess'));
              navigate('..');
            } catch (err) {
              captureException(err);
              toast.error(
                i18n('carrierPortal.contacts.updateContactError', {
                  error: getDisplayError(err),
                }),
              );
            }
          }}
        >
          {({ handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <InputField labelText={i18n('carrierPortal.contacts.name')} type="text" name="name" />
              <InputField labelText={i18n('carrierPortal.contacts.email')} type="email" name="email" />
              <InputField labelText={i18n('carrierPortal.contacts.phone')} type="text" name="phone" />
              <InputField labelText={i18n('carrierPortal.contacts.function')} type="text" name="function" />
              <SimpleSelectField
                labelText={i18n('carrierPortal.contacts.language')}
                items={LANGUAGE_VALUES}
                name="language"
              />

              <h2 className="heading-two mb-2">{i18n('carrierPortal.contacts.notifications')}</h2>
              <div className="grid grid-cols-4 gap-x-4 mb-2">
                <CheckboxField
                  labelText={i18n('carrierContactNotifications.transportOrders')}
                  name="shouldReceiveTransportOrders"
                />
                <CheckboxField
                  labelText={i18n('carrierContactNotifications.selfBillings')}
                  name="shouldReceiveSelfBillings"
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  type="submit"
                  color="primary"
                  iconLeft={<PlusIcon className="button-icon" />}
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  {i18n('carrierPortal.contacts.updateContact')}
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};
